import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useEngine } from '@/lib/motion/section'
import { counterStarts } from '@/constants/content'

const RADIUS = 20.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const NUMBERS = [1, 2, 3, 4, 5, 6]

const SectionCounter: FC = (): ReactNode => {
    const stripRef = useRef<HTMLDivElement>(null)
    const arcRef = useRef<SVGCircleElement>(null)

    useEngine(() => {
        let starts: Array<number> = []
        let current = -1
        let lastDash = -1

        return {
            measure: () => {
                starts = counterStarts.map((id) => {
                    const element = document.getElementById(id)

                    return element ? element.getBoundingClientRect().top + window.scrollY : 0
                })
            },
            frame: (state) => {
                const probe = state.scroll + state.vh * 0.5
                let index = 0

                for (let i = 0; i < starts.length; i++) {
                    if (probe >= starts[i]) index = i
                }

                if (index !== current && stripRef.current) {
                    current = index
                    stripRef.current.style.transform = `translate3d(0, ${(-index * 100) / NUMBERS.length}%, 0)`
                }

                const progress = state.maxScroll > 0 ? Math.min(1, state.scroll / state.maxScroll) : 0
                const dash = CIRCUMFERENCE * (1 - progress)

                if (Math.abs(dash - lastDash) > 0.05 && arcRef.current) {
                    lastDash = dash
                    arcRef.current.style.strokeDashoffset = String(dash)
                }
            }
        }
    })

    return (
        <div aria-hidden='true' className='pointer-events-none fixed right-4 top-[4.6rem] z-30 size-11 md:right-6'>
            <svg viewBox='0 0 44 44' className='absolute inset-0 size-full -rotate-90'>
                <circle cx='22' cy='22' r={RADIUS} fill='rgba(246,248,248,0.7)' stroke='#d9dfe2' strokeWidth='1' />
                <circle
                    ref={arcRef}
                    cx='22'
                    cy='22'
                    r={RADIUS}
                    fill='none'
                    stroke='#1d2a31'
                    strokeWidth='1'
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={CIRCUMFERENCE}
                />
            </svg>

            <div className='absolute inset-[5px] overflow-hidden rounded-full'>
                <div ref={stripRef} className='transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]'>
                    {NUMBERS.map(number => (
                        <div key={number} className='flex h-[34px] items-center justify-center font-mono text-[11px] tabular-nums text-ink'>
                            {number}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default SectionCounter
